/**
 * Página principal da ferramenta "Calcular Orçamento".
 *
 * ✅ SIMPLIFICADO: Fluxo de exatamente 4 telas
 *
 * FLUXO:
 * 1. PROGRESSBAR (loading) → Coleta objetos do C# + busca regras via REST
 * 2. MATERIAL → Confirma material detectado ou seleciona manualmente
 * 3. QUALIDADE → Seleciona qualidade do material
 * 4. RESUMO → Exibe detalhes do orçamento
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { sendToTerminal, logger } from '../../../lib/terminalLogger';
import { LogCategory } from '../../../config/logging';
import { PreparationOverlay } from '../../common/PreparationOverlay';
import { MaterialConfirmModal, MaterialSelectModal, QualitySelectModal, QuotationSummaryPage } from './components';
import { QuotationOrderDetailsLauncher } from './components/QuotationOrderDetailsLauncher';
import { QuotationOperationContextStore } from './services/quotationOperationContext';
import type { Cliente } from '../../../types/gerArquivos';
import type { CreatedQuotationOrderData } from './components';
import type { Quality } from './components/QualitySelectModal';
import type { ProductRule, QuotationPageState, OrderObject, MaterialDetectionResult, QuotationSaveNowSession, SavingRule } from './types';
import type { Pedido } from '../../../contexts/OrderContext';
import { useOrders } from '../../../contexts/OrderContext';
import { useNotifications } from '../../../components/feedback/NotificationProvider';
import { runQuotationFlowPipeline } from './services/quotationFlowPipeline';
import { useSavingRules, useQualities } from '../hooks';
import { useSimulatedProgress, QUOTATION_STEPS } from './hooks/useSimulatedProgress';
import { readPreparedPipelineId, setPreparedPipelineId } from '../../../lib/corel/pipeline/pipelineKeys';
import {
  buildClosingDeliveryDate,
  mapClosingDeliveryToOrderDetailsValue,
} from './utils/quotationClosingHandoff';

// IDs fixos para o orçamento (usados no snapshot e contexto)
const QUOTATION_PEDIDO_ID = 'quotation';
const QUOTATION_ITEM_ID = 'quotation-item';

function buildQuotationProductRules(
  selectedRule: ProductRule | null,
  savingRule: SavingRule | null,
) {
  const smb = selectedRule?.seguranca?.smb
    ? {
      username: selectedRule.seguranca.smb.username || undefined,
      password_enc: selectedRule.seguranca.smb.password_enc || undefined,
      domain: selectedRule.seguranca.smb.domain || null,
    }
    : undefined;

  return {
    pro_id: selectedRule?.produto?.id ?? 0,
    umd_id: savingRule?.umd_id ?? 3,
    nome_servico: selectedRule?.produto?.nome_servico || 'Material',
    descricao: selectedRule?.produto?.descricao || '',
    horario_corte:
      savingRule?.horario_corte || selectedRule?.dimensoes?.horario_corte || '18:00',
    largura_maxima_cm: selectedRule?.dimensoes?.largura_cm ?? null,
    altura_maxima_cm: selectedRule?.dimensoes?.altura_cm ?? null,
    diretorio_base_interno:
      savingRule?.diretorio_base_interno || selectedRule?.diretorios?.producao || '',
    diretorio_base_externo:
      savingRule?.diretorio_base_externo || selectedRule?.diretorios?.layout_png || '',
    diretorio_base_exportpng:
      savingRule?.diretorio_base_exportpng || selectedRule?.diretorios?.layout_png || '',
    diretorio_base_export_prod:
      savingRule?.diretorio_base_export_prod || selectedRule?.diretorios?.producao || '',
    diretorio_nome:
      savingRule?.diretorio_nome || selectedRule?.diretorios?.nome || '',
    regra_id: selectedRule?.regra?.id,
    regra_ativo: selectedRule?.regra?.ativo,
    diretorios: {
      producao:
        savingRule?.diretorio_base_export_prod || selectedRule?.diretorios?.producao || '',
      layout_png:
        savingRule?.diretorio_base_exportpng || selectedRule?.diretorios?.layout_png || '',
      nome: savingRule?.diretorio_nome || selectedRule?.diretorios?.nome || '',
    },
    seguranca: smb ? { smb } : undefined,
    security: smb
      ? {
        interno: { smb },
        externo: { smb },
        exportpng: { smb },
        diretorio_base_interno: { smb },
      }
      : undefined,
    exportacao: {
      formato: 'PNG',
      resolucao_dpi: 300,
      cores: 'CMYK',
      compressao: 'PNG',
      sangria_mm: 3,
      icc_profile: 'ISO Coated v2 (ECI)',
    },
    padroes_cdr: {
      versao_arquivo: '20.0',
      perfil_icc: 'ISO Coated v2 (ECI)',
      compactar: true,
      salvar_com_visualizacao: true,
    },
    mensagens: {
      hoje: 'Entrega hoje',
      amanha: 'Entrega amanha',
      fds: 'Entrega segunda-feira',
      redesenho: 'Aguardando redesenho',
    },
  };
}


interface QuotationCalculatorPageProps {
  onBack: () => void;
  onOpenOrderDetails?: (pedido: any, itemId: string) => void;
}

/**
 * Definição dos steps para o PreparationOverlay (simplificado)
 */
const PREPARATION_STEPS = [
  { id: 'rules', label: 'Carregando materiais', description: 'Buscando regras de produtos...' },
  { id: 'collect', label: 'Coletando objetos', description: 'Coletando objetos da seleção...' },
  { id: 'detect', label: 'Detectando material', description: 'Analisando dimensões...' },
];

export function QuotationCalculatorPage({ onBack, onOpenOrderDetails }: QuotationCalculatorPageProps) {
  // ═══════════════════════════════════════════════════════════════════════════
  // CONTEXTO E ESTADOS (igual SalesOrdersPage)
  // ═══════════════════════════════════════════════════════════════════════════

  const { fetchPedidos, selectPedido, updatePedidoInList } = useOrders();
  const { notify } = useNotifications();

  // ✅ PADRONIZADO: Usar hooks centralizados
  const { rules: savingRules } = useSavingRules();

  const [preparing, setPreparing] = useState(false); // ✅ Controle local igual SalesOrdersPage
  const [pageState, setPageState] = useState<QuotationPageState>('idle');
  // Cliente roteado (cross-tenant) — selecionado dentro do summary.
  const [routedClient, setRoutedClient] = useState<Cliente | null>(null);
  const [selectedRule, setSelectedRule] = useState<ProductRule | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [retryMessage, setRetryMessage] = useState<string | null>(null); // ✅ Mensagem de retry para UI
  const [collectedObjects, setCollectedObjects] = useState<OrderObject[]>([]);
  const [detectionResult, setDetectionResult] = useState<MaterialDetectionResult | null>(null);
  const [savingRule, setSavingRule] = useState<SavingRule | null>(null); // ✅ Regra completa de salvamento
  const [rules, setRules] = useState<ProductRule[]>([]); // ✅ Regras de produtos carregadas
  const [selectedQuality, setSelectedQuality] = useState<Quality | null>(null); // ✅ Qualidade selecionada pelo usuário
  const [saveNowSession, setSaveNowSession] = useState<QuotationSaveNowSession | null>(null);

  // ✅ PROGRESSBAR HÍBRIDA: Hook para gerenciar progresso simulado + real
  const {
    progress: simulatedProgress,
    currentStep,
    detailedProgress,
    isComplete: progressIsComplete,
    updateRealProgress,
    completeStep,
    setStep,
    reset: resetProgress,
    forceComplete: forceProgressComplete,
  } = useSimulatedProgress(QUOTATION_STEPS);

  // Ref para armazenar resultado pendente da pipeline
  const pendingPipelineResultRef = useRef<{
    objects: OrderObject[];
    rules: ProductRule[];
    detectionResult: MaterialDetectionResult | null;
  } | null>(null);

  // ✅ REFATORADO: Usar useQualities hook diretamente sem estado duplicado
  // categoriaId vem de selectedRule.produto?.categoria_item
  const categoriaIdForHook = selectedRule?.produto?.categoria_item ?? null;
  const { qualities: qualitiesFromHook, loading: qualitiesLoadingFromHook, error: qualitiesErrorFromHook, refresh: refreshQualities, hasAttempted: qualitiesHasAttemptedFromHook } = useQualities({
    categoriaId: categoriaIdForHook,
    disableDebounce: false // Usar debounce para evitar requisições múltiplas
  });

  // ✅ REFATORADO: Refs necessárias apenas para cancelamento e verificação de montagem
  const isMountedRef = useRef(true);
  const isProcessingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null); // ✅ Ref para cancelar pipeline
  const abortControllerCreatedAtRef = useRef<number | null>(null); // ✅ Ref para rastrear quando abortController foi criado

  // ═══════════════════════════════════════════════════════════════════════════
  // FLUXO PRINCIPAL (igual estrutura da SalesOrdersPage)
  // ═══════════════════════════════════════════════════════════════════════════

  const startFlow = useCallback(async () => {
    sendToTerminal('🔧 [QUOTATION:startFlow] ENTRADA na função startFlow()', {
      preparing,
      isProcessingRef: isProcessingRef.current,
      isMountedRef: isMountedRef.current
    }, LogCategory.BUSINESS);

    // 🔒 Bloquear se já está preparando (múltiplas verificações)
    if (preparing || isProcessingRef.current) {
      sendToTerminal('⚠️ [QUOTATION:startFlow] BLOCKED (já em andamento)', {
        preparing,
        isProcessing: isProcessingRef.current
      }, LogCategory.BUSINESS);
      return;
    }

    // ✅ Verificar se componente ainda está montado
    if (!isMountedRef.current) {
      sendToTerminal('⚠️ [QUOTATION:startFlow] BLOCKED (componente desmontado)', {}, LogCategory.BUSINESS);
      return;
    }

    // ✅ Ativar locks ANTES de qualquer operação
    isProcessingRef.current = true;
    setPreparing(true);
    setPageState('loading'); // ✅ SIMPLIFICADO: Estado único de loading
    resetProgress(); // ✅ Resetar progressbar híbrida
    setErrorMessage(null);
    setCollectedObjects([]);
    setDetectionResult(null);
    setSavingRule(null);
    setSelectedQuality(null);
    pendingPipelineResultRef.current = null; // ✅ Limpar resultado pendente

    sendToTerminal('🚀 [QUOTATION:startFlow] Locks ativados, iniciando pipeline', {
      isProcessingRef: isProcessingRef.current,
      preparing: true
    }, LogCategory.BUSINESS);
    logger.info('[QUOTATION] Pipeline iniciada');

    // ✅ Criar AbortController para cancelamento (armazenar na ref para acesso externo)
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    abortControllerCreatedAtRef.current = Date.now(); // ✅ Rastrear quando foi criado

    try {
      // ✅ USAR PIPELINE ROBUSTA
      const result = await runQuotationFlowPipeline({
        abortSignal: abortController.signal,
        onProgress: (event) => {
          // ✅ PROGRESSBAR HÍBRIDA: Usar hook para controlar progresso
          // Atualiza step visual e progresso detalhado via hook
          if (event.stage === 'rules:loading' || event.stage === 'rules:retry') {
            setStep('rules');
            // ✅ Feedback visual durante retry
            if (event.stage === 'rules:retry' && event.retryAttempt && event.maxRetries) {
              const msg = `Tentando novamente (${event.retryAttempt}/${event.maxRetries})...`;
              setRetryMessage(msg);
              sendToTerminal(`🔄 [QUOTATION] ${msg}`, {
                attempt: event.retryAttempt,
                maxRetries: event.maxRetries,
                error: event.meta?.error
              }, LogCategory.BUSINESS);
            } else if (event.stage === 'rules:loading') {
              setRetryMessage(null);
            }
          } else if (event.stage === 'rules:loaded') {
            // ✅ Step 'rules' concluído
            completeStep('rules');
            setRetryMessage(null);
          } else if (event.stage === 'collect:loading' || event.stage === 'collect:retry') {
            setStep('collect');
            // ✅ Feedback visual durante retry
            if (event.stage === 'collect:retry' && event.retryAttempt && event.maxRetries) {
              const msg = `Tentando coletar objetos novamente (${event.retryAttempt}/${event.maxRetries})...`;
              setRetryMessage(msg);
              sendToTerminal(`🔄 [QUOTATION] ${msg}`, {
                attempt: event.retryAttempt,
                maxRetries: event.maxRetries,
                error: event.meta?.error
              }, LogCategory.BUSINESS);
            } else if (event.stage === 'collect:loading') {
              setRetryMessage(null);
            }
          } else if (event.stage === 'collect:progress') {
            // ✅ Progresso detalhado da coleta de metadados (vindo do C#)
            const meta = event.meta as Record<string, unknown> | undefined;
            if (meta) {
              updateRealProgress({
                percentComplete: (meta.percentComplete as number) ?? 0,
                message: (meta.message as string) ?? 'Coletando objetos...',
                current: (meta.current as number) ?? 0,
                total: (meta.total as number) ?? 0
              });
            }
          } else if (event.stage === 'collect:loaded') {
            // ✅ Step 'collect' concluído
            completeStep('collect');
            setRetryMessage(null);
          } else if (event.stage === 'detection:starting') {
            setStep('detect');
            setRetryMessage(null);
          } else if (event.stage === 'detection:success' || event.stage === 'detection:no_match') {
            // ✅ Step 'detect' concluído - último step (sucesso ou sem match)
            completeStep('detect');
          }
        }
      });

      if (result.status === 'success' && result.data) {
        // ✅ Pipeline concluída com sucesso - armazenar dados
        setCollectedObjects(result.data.objects);
        setDetectionResult(result.data.detectionResult);
        setRules(result.data.rules);

        // ✅ PROGRESSBAR HÍBRIDA: Armazenar resultado para processar após animação
        // A transição de tela só acontece quando progressIsComplete = true
        pendingPipelineResultRef.current = {
          objects: result.data.objects,
          rules: result.data.rules,
          detectionResult: result.data.detectionResult
        };

        sendToTerminal('✅ [QUOTATION] Pipeline concluída - Aguardando progressbar', {
          objetosCount: result.data.objects.length,
          materialDetectado: result.data.detectionResult?.rule.produto?.nome_servico ?? 'Não detectado',
          confianca: result.data.detectionResult ? `${result.data.detectionResult.confidence}%` : 'N/A',
          metrics: result.metrics
        }, LogCategory.BUSINESS);
      } else if (result.status === 'cancelled') {
        // Pipeline cancelada
        forceProgressComplete(); // ✅ Forçar conclusão da progressbar
        sendToTerminal('⚠️ [QUOTATION] Pipeline cancelada', {
          reason: result.error?.message
        }, LogCategory.BUSINESS);
        setPageState('idle');
      } else {
        // Pipeline falhou
        forceProgressComplete(); // ✅ Forçar conclusão da progressbar
        const errorMsg = result.error?.message || 'Erro desconhecido na pipeline';
        const errorCode = result.error?.code || 'unknown_error';

        sendToTerminal('❌ [QUOTATION] Pipeline falhou - definindo mensagem de erro', {
          errorMessage: errorMsg,
          errorCode,
          hasErrorMessage: !!errorMsg,
          errorObject: result.error
        }, LogCategory.ERROR);

        notify({
          type: 'error',
          title: 'Erro ao Calcular Orçamento',
          message: errorMsg,
          sticky: false,
          duration: 10000
        });

        setErrorMessage(errorMsg);
        setPageState('error');

        sendToTerminal('✅ [QUOTATION] Mensagem de erro definida na UI', {
          errorMessage: errorMsg,
          pageState: 'error',
          errorCode
        }, LogCategory.BUSINESS);

        logger.error('[QUOTATION] Pipeline falhou', {
          error: result.error,
          metrics: result.metrics
        });
      }

    } catch (error) {
      forceProgressComplete(); // ✅ Forçar conclusão da progressbar
      const errorMsg = error instanceof Error ? error.message : 'Erro inesperado';
      const errorStack = error instanceof Error ? error.stack : undefined;

      // ✅ UX: Notificar usuário sobre erro
      notify({
        type: 'error',
        title: 'Erro ao Calcular Orçamento',
        message: errorMsg,
        sticky: false,
        duration: 10000
      });

      setErrorMessage(errorMsg);
      setPageState('error');

      sendToTerminal('❌ [QUOTATION] Exceção na pipeline', {
        error: errorMsg,
        stack: errorStack?.split('\n').slice(0, 5).join(' → ')
      }, LogCategory.ERROR);

      logger.error('[QUOTATION] Exceção não tratada', {
        error: errorMsg,
        stack: error instanceof Error ? error.stack : undefined
      });
    } finally {
      // ✅ SEMPRE liberar locks no finally
      isProcessingRef.current = false;
      setPreparing(false);
      abortControllerRef.current = null; // ✅ Limpar referência do abortController
      abortControllerCreatedAtRef.current = null; // ✅ Limpar timestamp
    }
  }, [preparing, notify, resetProgress, setStep, completeStep, updateRealProgress, forceProgressComplete]);

  // ✅ Cleanup ao desmontar
  useEffect(() => {
    sendToTerminal('🔧 [QUOTATION:MOUNT] Componente montado', {
      isMountedRef: isMountedRef.current,
      isProcessingRef: isProcessingRef.current
    }, LogCategory.BUSINESS);

    isMountedRef.current = true;
    return () => {
      sendToTerminal('🔧 [QUOTATION:UNMOUNT] Componente desmontado (cleanup)', {}, LogCategory.BUSINESS);
      isMountedRef.current = false;

      // ✅ CORRIGIDO: Não abortar se abortController foi criado muito recentemente (< 200ms)
      // Isso evita abortar durante StrictMode onde o cleanup executa imediatamente após mount
      if (abortControllerRef.current && abortControllerCreatedAtRef.current) {
        const timeSinceCreation = Date.now() - abortControllerCreatedAtRef.current;
        const isRecentCreation = timeSinceCreation < 200; // Menos de 200ms = provavelmente StrictMode

        if (isRecentCreation) {
          sendToTerminal('⚠️ [QUOTATION:UNMOUNT] Ignorando abort (criação muito recente - provavelmente StrictMode)', {
            timeSinceCreationMs: timeSinceCreation
          }, LogCategory.BUSINESS);
          // Não abortar - provavelmente é StrictMode
          // O abortController será limpo quando a pipeline terminar naturalmente
        } else {
          sendToTerminal('🛑 [QUOTATION:UNMOUNT] Abortando pipeline (unmount real)', {
            timeSinceCreationMs: timeSinceCreation
          }, LogCategory.BUSINESS);
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
          abortControllerCreatedAtRef.current = null;
          abortControllerCreatedAtRef.current = null;
        }
      }
    };
  }, []);

  // ✅ Iniciar fluxo ao montar (StrictMode já trata dupla execução).
  useEffect(() => {
    if (preparing || isProcessingRef.current) {
      return;
    }
    if (!isMountedRef.current) {
      return;
    }
    sendToTerminal('🚀 [QUOTATION:INIT] Iniciando pipeline ao montar', {}, LogCategory.BUSINESS);
    startFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpa o contexto de operação roteado ao desmontar a ferramenta.
  useEffect(() => {
    return () => {
      QuotationOperationContextStore.clear();
    };
  }, []);

  // ✅ PROGRESSBAR HÍBRIDA: Transição de tela após animação completa
  useEffect(() => {
    // Só processar quando progressbar completar E tivermos resultado pendente
    if (!progressIsComplete || !pendingPipelineResultRef.current) {
      return;
    }

    const pending = pendingPipelineResultRef.current;
    pendingPipelineResultRef.current = null; // Limpar ref antes de transição

    sendToTerminal('🎬 [QUOTATION] Progressbar completa - Iniciando transição', {
      hasDetectionResult: !!pending.detectionResult,
      objectsCount: pending.objects.length,
      rulesCount: pending.rules.length
    }, LogCategory.BUSINESS);

    // ✅ Transição para próxima tela baseado no resultado
    if (pending.detectionResult) {
      setPageState('confirmMaterial');
      sendToTerminal('✅ [QUOTATION] Transição → confirmMaterial', {
        material: pending.detectionResult.rule.produto?.nome_servico,
        confianca: `${pending.detectionResult.confidence}%`
      }, LogCategory.BUSINESS);
    } else {
      setPageState('selectMaterial');
      sendToTerminal('✅ [QUOTATION] Transição → selectMaterial', {
        rulesCount: pending.rules.length
      }, LogCategory.BUSINESS);
    }
  }, [progressIsComplete]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Abre OrderDetailsWindow com a qualidade selecionada (ou sem qualidade se null)
   * ⚠️ DEVE ser declarado ANTES de openOrderDetailsWithRule
   * 
   * @param quality - Qualidade selecionada (ou null para pular)
   * @param ruleOverride - ✅ CORREÇÃO: Regra passada diretamente para evitar race condition com setState
   * @param savingRuleOverride - ✅ CORREÇÃO: Regra de salvamento passada diretamente
   */
  const openOrderDetailsWithQuality = useCallback((
    quality: Quality | null,
    ruleOverride?: ProductRule | null,
    savingRuleOverride?: SavingRule | null
  ) => {
    // ✅ CORREÇÃO: Usar valor passado diretamente OU estado (fallback)
    const effectiveRule = ruleOverride ?? selectedRule;
    const effectiveSavingRule = savingRuleOverride ?? savingRule;

    sendToTerminal('🔧 [QUOTATION:openOrderDetailsWithQuality] ENTRADA', {
      hasQuality: !!quality,
      hasRuleOverride: !!ruleOverride,
      hasSavingRuleOverride: !!savingRuleOverride,
      effectiveRuleId: effectiveRule?.produto?.id,
      effectiveSavingRuleUmdId: effectiveSavingRule?.umd_id
    }, LogCategory.BUSINESS);

    // ✅ Validação: verificar se componente ainda está montado
    if (!isMountedRef.current) {
      sendToTerminal('⚠️ [QUOTATION] openOrderDetailsWithQuality cancelado (componente desmontado)', {}, LogCategory.BUSINESS);
      return;
    }

    // ✅ Validação: verificar se effectiveRule está definido
    if (!effectiveRule) {
      sendToTerminal('❌ [QUOTATION] Erro: selectedRule não definido', {
        selectedRuleState: !!selectedRule,
        ruleOverride: !!ruleOverride
      }, LogCategory.ERROR);
      setErrorMessage('Erro: material não selecionado. Tente novamente.');
      setPageState('error');
      return;
    }

    // ✅ Validação: verificar se effectiveSavingRule está definido (necessário para cálculo)
    if (!effectiveSavingRule) {
      sendToTerminal('⚠️ [QUOTATION] savingRule não definido, usando padrão', {}, LogCategory.BUSINESS);
      // Não bloquear, mas avisar
    }

    const proId = effectiveRule.produto?.id ?? 0;
    const umdId = effectiveSavingRule?.umd_id ?? 3;

    // ═══════════════════════════════════════════════════════════════════════
    // CRIAR PEDIDO FICTÍCIO COM QUALIDADE
    // ═══════════════════════════════════════════════════════════════════════
    const quotationPedido = {
      orc_id: QUOTATION_PEDIDO_ID,
      cliente: {
        cli_id: 0,
        nome: 'Orçamento Rápido',
        razao_social: 'Orçamento Rápido',
        nome_fantasia: 'Orçamento Rápido'
      },
      itens: [{
        oit_id: QUOTATION_ITEM_ID,
        quantidade: 0, // ✅ 0 = orçamento (não há quantidade vendida)
        qualidade: quality ? quality.one1_nome : 'standard', // ✅ Qualidade selecionada
        contracao: '0px',
        entrega: 'normal',
        produto: {
          pro_id: proId,
          nome: effectiveRule.produto?.nome_servico ?? 'Material',
          // ✅ Incluir unidade para cálculo correto
          unidade: {
            umd_id: umdId,
            nome: umdId === 3 ? 'Metros' : 'Unidade'
          }
        },
        material: {
          mat_id: 0,
          nome: 'Padrão'
        },
        regras: {
          largura_maxima_cm: effectiveRule.dimensoes?.largura_cm ?? 0,
          altura_maxima_cm: effectiveRule.dimensoes?.altura_cm ?? 0
        }
      }]
    };

    sendToTerminal('📦 [QUOTATION] Pedido fictício criado com qualidade', {
      proId,
      umdId,
      qualidade: quality?.one1_nome ?? 'N/A',
      tipoCalculo: umdId === 3 ? 'Metragem (altura)' : 'Contagem de objetos'
    }, LogCategory.BUSINESS);

    // ✅ Verificar novamente se componente está montado antes de setar estados
    if (!isMountedRef.current) {
      sendToTerminal('⚠️ [QUOTATION] openOrderDetailsWithQuality cancelado antes de setar estados', {}, LogCategory.BUSINESS);
      return;
    }

    // ✅ Usar setTimeout para garantir que estados anteriores foram aplicados
    // Isso evita race conditions onde o estado muda antes do componente processar
    setTimeout(() => {
      if (!isMountedRef.current) return;

      // Salvar no contexto
      selectPedido(quotationPedido as any);
      setSelectedQuality(quality);
      setPageState('showSummary'); // ✅ Atualiza pageState para mostrar resumo

      sendToTerminal('✅ [QUOTATION] Página de resumo aberta', {
        proId,
        umdId,
        qualidade: quality?.one1_nome || 'N/A'
      }, LogCategory.BUSINESS);
      }, 0);
  }, [selectedRule, savingRule, selectPedido]);

  const handleRealOrderCreated = useCallback((data: CreatedQuotationOrderData) => {
    const preparedPipelineId = readPreparedPipelineId(QUOTATION_PEDIDO_ID, QUOTATION_ITEM_ID);
    if (preparedPipelineId && data.oitId) {
      setPreparedPipelineId(data.orcId, data.oitId, preparedPipelineId);
    }

    const productRules = buildQuotationProductRules(selectedRule, savingRule);
    const isMetros = data.unitId === 3;
    const quantityNormalized = Number.isFinite(data.quantity) ? data.quantity : 0;
    const deliveryOption = data.entrega || 'normal';

    const realPedido = {
      orc_id: data.orcId,
      ost_id: 1,
      observacao:
        data.observacaoPedido?.trim() || 'Orcamento registrado via cotacao CorelDRAW',
      nome_arquivo: null,
      valor_total: data.totalValue.toFixed(2),
      data_entrega: buildClosingDeliveryDate(deliveryOption),
      data_cadastro: null,
      data_emissao: null,
      valor_frete: '0.00',
      forma_pagamento_id: data.formaPagamentoId ?? 11,
      valor_desconto: '0.00',
      cliente: {
        cli_id: data.clientId,
        nome: data.clientName,
        razao_social: data.clientName,
        nome_fantasia: data.clientName,
      },
      status: {
        id_pagamento: 1,
        orc_status_id: data.formaPagamentoId ?? 1,
        descricao: 'Aguardando pagamento',
        descricao_pagamento: 'Aguardando pagamento',
      },
      status_orcamento: {
        id: 1,
        descricao: 'Orçamento',
      },
      itens: [
        {
          oit_id: data.oitId,
          quantidade: quantityNormalized,
          observacao: data.observacaoItem?.trim() || '',
          estagio: 'orcamento',
          valor_unitario: data.unitPrice.toFixed(2),
          valor_total: data.totalValue.toFixed(2),
          status: {
            oit_status_id: 1,
            descricao: 'Aguardando',
          },
          qualidade: data.qualityName || selectedQuality?.one1_nome || 'standard',
          contracao: '0px',
          entrega: deliveryOption,
          entrega_opcao: mapClosingDeliveryToOrderDetailsValue(deliveryOption),
          produto: {
            pro_id: data.productId,
            nome: data.productName,
            tipo: 1,
            categoria: data.categoriaItem,
            quantidade: quantityNormalized,
            unidade: {
              umd_id: data.unitId,
              descricao: isMetros ? 'Metros' : 'Unidade',
              sigla: isMetros ? 'm' : 'un',
            },
          },
          material: {
            mat_id: 0,
            nome: data.qualityName || selectedQuality?.one1_nome || 'Padrao',
          },
          regras: productRules,
        },
      ],
    } as Pedido & { ost_id?: number; status_orcamento?: { id: number; descricao: string } };

    updatePedidoInList(realPedido as any);
    selectPedido(realPedido as any);
    void fetchPedidos(true).catch((error) => {
      sendToTerminal('⚠️ [QUOTATION] Falha ao sincronizar lista de pedidos após criar orçamento', {
        orcId: data.orcId,
        error: error instanceof Error ? error.message : String(error)
      }, LogCategory.ERROR);
    });

    sendToTerminal('✅ [QUOTATION] Pedido de contexto atualizado com IDs reais', {
      oldOrcId: QUOTATION_PEDIDO_ID,
      oldOitId: QUOTATION_ITEM_ID,
      newOrcId: data.orcId,
      newOitId: data.oitId,
      preparedPipelineMigrated: !!preparedPipelineId && !!data.oitId,
      preparedPipelineId: preparedPipelineId || null
    }, LogCategory.BUSINESS);
    if (data.autoOpenPipeline && data.oitId) {
      sendToTerminal('🚀 [QUOTATION] Abrindo launcher de salvamento direto da cotacao', {
        orcId: data.orcId,
        oitId: data.oitId
      }, LogCategory.BUSINESS);

      setSaveNowSession({
        pedido: realPedido as Pedido,
        pedidoId: data.orcId,
        itemId: data.oitId,
        initialDelivery: mapClosingDeliveryToOrderDetailsValue(deliveryOption),
        origin: 'quotation',
        onClose: () => {
          setSaveNowSession(null);
          onBack();
        }
      });
    } else if (data.autoOpenPipeline && !data.oitId) {
      sendToTerminal('ℹ️ [QUOTATION] Pipeline automático não aberto: orçamento sem oit_id real', {
        orcId: data.orcId
      }, LogCategory.BUSINESS);
    }
  }, [fetchPedidos, onBack, savingRule, selectPedido, selectedQuality, selectedRule, updatePedidoInList]);

  const handleSaveNowRequested = useCallback((data: CreatedQuotationOrderData) => {
    handleRealOrderCreated({
      ...data,
      autoOpenPipeline: true
    });
  }, [handleRealOrderCreated]);

  /**
   * Cria pedido fictício e prepara para seleção de qualidade
   * ✅ SIMPLIFICADO: Mostra loading imediatamente, transição via useEffect
   */
  const openOrderDetailsWithRule = useCallback((rule: ProductRule) => {
    // ✅ Validação: verificar se componente ainda está montado
    if (!isMountedRef.current) {
      sendToTerminal('⚠️ [QUOTATION] openOrderDetailsWithRule cancelado (componente desmontado)', {}, LogCategory.BUSINESS);
      return;
    }

    // ✅ Validação: verificar se rule é válida
    if (!rule || !rule.produto?.id) {
      sendToTerminal('❌ [QUOTATION] openOrderDetailsWithRule: regra inválida', { rule }, LogCategory.ERROR);
      setErrorMessage('Regra de material inválida. Tente novamente.');
      setPageState('error');
      return;
    }

    const proId = rule.produto?.id ?? 0;
    const categoriaId = rule.produto?.categoria_item;

    // ═══════════════════════════════════════════════════════════════════════
    // BUSCAR REGRA DE SALVAMENTO (apenas para umd_id e outras infos)
    // ═══════════════════════════════════════════════════════════════════════
    let umdId = 3; // Default: metros

    try {
      // ✅ PADRONIZADO: Usar saving rules do hook (já carregadas com cache)
      const matchingRule = savingRules.find(sr => sr.pro_id === proId);

      if (matchingRule) {
        umdId = matchingRule.umd_id;
        setSavingRule(matchingRule);

        sendToTerminal('✅ [QUOTATION] Regra de salvamento encontrada', {
          proId,
          umdId,
          tipoUnidade: umdId === 3 ? 'METROS' : 'UNIDADES',
          nomeServico: matchingRule.nome_servico,
          diretorioNome: matchingRule.diretorio_nome
        }, LogCategory.BUSINESS);
      } else {
        sendToTerminal('⚠️ [QUOTATION] Regra de salvamento não encontrada, usando padrão (metros)', {
          proId,
          umdIdDefault: umdId
        }, LogCategory.BUSINESS);
        setSavingRule(null);
      }
    } catch (error) {
      sendToTerminal('⚠️ [QUOTATION] Erro ao buscar regra de salvamento (usando padrão)', {
        error: String(error),
        proId
      }, LogCategory.ERROR);
      setSavingRule(null);
    }

    sendToTerminal('📦 [QUOTATION] Preparando para carregar qualidades', {
      proId,
      umdId,
      tipoCalculo: umdId === 3 ? 'Metragem (altura)' : 'Contagem de objetos',
      hasCategoriaId: !!categoriaId
    }, LogCategory.BUSINESS);

    // ✅ Salvar regra selecionada (isso dispara o hook useQualities)
    setSelectedRule(rule);

    // ✅ Ir direto para selectQuality - o modal mostrará loading interno
    setPageState('selectQuality');

    sendToTerminal('⏳ [QUOTATION] Abrindo modal de qualidades', {
      categoriaId,
      proId
    }, LogCategory.API);
  }, [savingRules]);

  const handleConfirmMaterial = useCallback(() => {
    if (detectionResult) {
      sendToTerminal('✅ [QUOTATION] Material confirmado', {
        material: detectionResult.rule.produto?.nome_servico
      }, LogCategory.BUSINESS);

      // ✅ Carregar qualidades e ir para próxima etapa
      openOrderDetailsWithRule(detectionResult.rule);
    }
  }, [detectionResult, openOrderDetailsWithRule]);

  const handleRejectMaterial = useCallback(() => {
    setPageState('selectMaterial');
    sendToTerminal('🔄 [QUOTATION] Material rejeitado - seleção manual', {}, LogCategory.BUSINESS);
  }, []);

  const handleSelectMaterial = useCallback((rule: ProductRule) => {
    sendToTerminal('✅ [QUOTATION] Material selecionado', {
      material: rule.produto?.nome_servico
    }, LogCategory.BUSINESS);

    // ✅ Carregar qualidades e ir para próxima etapa
    openOrderDetailsWithRule(rule);
  }, [openOrderDetailsWithRule]);

  const handleSelectQuality = useCallback((quality: Quality) => {
    sendToTerminal('⭐ [QUOTATION] Qualidade selecionada', {
      qualidade: quality.one1_nome,
      qualidadeId: quality.id
    }, LogCategory.BUSINESS);

    openOrderDetailsWithQuality(quality);
  }, [openOrderDetailsWithQuality]);

  /**
   * ✅ REFATORADO: Função de retry para recarregar qualidades quando houver erro
   * Usa refresh do hook diretamente
   */
  const handleRetryQualities = useCallback(async () => {
    sendToTerminal('🔄 [QUOTATION] Retry qualidades iniciado', {
      selectedRuleId: selectedRule?.produto?.id,
      categoriaItem: selectedRule?.produto?.categoria_item,
      qualitiesCount: qualitiesFromHook.length
    }, LogCategory.BUSINESS);

    if (!selectedRule) {
      sendToTerminal('⚠️ [QUOTATION] Retry qualidades: regra não selecionada', {}, LogCategory.ERROR);
      return;
    }

    // ✅ Usar categoria_item diretamente do ProductRule (já vem de /cdr/rules/)
    const categoriaId = selectedRule.produto?.categoria_item;

    if (!categoriaId) {
      sendToTerminal('⚠️ [QUOTATION] Retry qualidades: categoria não encontrada no produto', {
        proId: selectedRule.produto?.id,
        produtoNome: selectedRule.produto?.nome_servico
      }, LogCategory.ERROR);

      notify({
        type: 'error',
        title: 'Categoria Não Encontrada',
        message: 'Este produto não possui categoria configurada. Selecione outro material.',
        sticky: false,
        duration: 5000
      });
      return;
    }

    sendToTerminal('🔄 [QUOTATION] Recarregando qualidades via hook...', {
      categoriaId,
      proId: selectedRule.produto?.id
    }, LogCategory.BUSINESS);

    try {
      // ✅ REFATORADO: Usar refresh do hook diretamente
      await refreshQualities();

      sendToTerminal('✅ [QUOTATION] Qualidades recarregadas com sucesso', {
        count: qualitiesFromHook.length
      }, LogCategory.BUSINESS);

      notify({
        type: 'success',
        title: 'Qualidades Carregadas',
        message: `${qualitiesFromHook.length} qualidade(s) disponível(is)`,
        sticky: false,
        duration: 3000
      });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);

      sendToTerminal('❌ [QUOTATION] Falha ao recarregar qualidades', {
        error: errorMsg
      }, LogCategory.ERROR);

      notify({
        type: 'error',
        title: 'Erro ao Carregar Qualidades',
        message: 'Falha ao conectar com o servidor. Tente novamente.',
        sticky: false,
        duration: 5000
      });
    }
  }, [selectedRule, qualitiesFromHook.length, refreshQualities, notify]);

  /**
   * ✅ CORREÇÃO: Função para lidar com ausência de qualidades
   * Não podemos pular porque o cálculo EXIGE qualidade_id > 0
   * Mostrar mensagem clara ao usuário
   */
  const handleSkipQuality = useCallback(() => {
    if (!selectedRule) {
      sendToTerminal('⚠️ [QUOTATION] Pular qualidade: regra não selecionada', {}, LogCategory.ERROR);
      return;
    }

    // ✅ Não podemos pular - o cálculo exige qualidade_id > 0
    // Tentar recarregar qualidades ou mostrar erro claro
    const categoriaId = selectedRule.produto?.categoria_item;
    sendToTerminal('⚠️ [QUOTATION] Tentando recarregar qualidades (não é possível pular)', {
      ruleId: selectedRule.produto?.id,
      categoriaId
    }, LogCategory.BUSINESS);

    // Tentar recarregar qualidades
    handleRetryQualities();
  }, [selectedRule, handleRetryQualities]);

  const handleRetry = useCallback(() => {
    sendToTerminal('🔄 [QUOTATION] Tentando novamente...', {}, LogCategory.BUSINESS);
    isProcessingRef.current = false;
    setErrorMessage(null);
    startFlow();
  }, [startFlow]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Loading com PreparationOverlay
  // ═══════════════════════════════════════════════════════════════════════════

  // ✅ SIMPLIFICADO: Estado único de loading
  const isLoading = pageState === 'loading';

  // ✅ Função para cancelar pipeline e voltar
  const handleCancelPipeline = useCallback(() => {
    sendToTerminal('🛑 [QUOTATION] Cancelando pipeline por solicitação do usuário', {
      pageState,
      isProcessing: isProcessingRef.current
    }, LogCategory.BUSINESS);

    // Abortar pipeline em andamento
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      abortControllerCreatedAtRef.current = null;
    }

    // Resetar estados
    isProcessingRef.current = false;
    setPreparing(false);
    setPageState('idle');
    setRetryMessage(null);

    // Chamar onBack para fechar a ferramenta
    onBack();
  }, [pageState, onBack]);

  if (isLoading) {
    // ✅ TELA 1: PROGRESSBAR - Loading com PreparationOverlay
    return (
      <PreparationOverlay
        visible={true}
        steps={PREPARATION_STEPS}
        currentStep={currentStep}
        error={null}
        title="Calculando Orçamento"
        retryMessage={retryMessage}
        detailedProgress={detailedProgress}
        progress={simulatedProgress} // ✅ Progresso global simulado (garante tempo mínimo de exibição)
        onClose={handleCancelPipeline}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: Erro com PreparationOverlay
  // ═══════════════════════════════════════════════════════════════════════════

  if (pageState === 'error') {
    // ✅ Log para debug - verificar se mensagem está sendo passada corretamente
    sendToTerminal('🎨 [QUOTATION] Renderizando modal de erro', {
      errorMessage,
      hasErrorMessage: !!errorMessage,
      errorMessageLength: errorMessage?.length || 0,
      currentStep
    }, LogCategory.BUSINESS);

    return (
      <PreparationOverlay
        visible={true}
        steps={PREPARATION_STEPS}
        currentStep={currentStep}
        error={errorMessage}
        title="Erro ao Calcular"
        onRetry={handleRetry}
        onClose={onBack}
      />
    );
  }

  if (saveNowSession) {
    return (
      <QuotationOrderDetailsLauncher
        session={saveNowSession}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: TELA 4 - Página de Resumo do Orçamento
  // ═══════════════════════════════════════════════════════════════════════════

  if (pageState === 'showSummary') {
    // ✅ Validação: só renderizar se TODOS os dados necessários estiverem prontos
    if (!selectedRule || !savingRule || collectedObjects.length === 0) {
      // Se dados não estão prontos, mostrar loading
      return (
        <PreparationOverlay
          visible={true}
          steps={PREPARATION_STEPS}
          currentStep="rules"
          error={null}
          title="Preparando dados do orçamento..."
        />
      );
    }
    return (
      <QuotationSummaryPage
        objects={collectedObjects}
        selectedRule={selectedRule}
        selectedQuality={selectedQuality}
        savingRule={savingRule}
        routedClient={routedClient}
        onOrderCreated={handleRealOrderCreated}
        onSaveNowRequested={handleSaveNowRequested}
        onBack={() => {
          // Limpar snapshot ao fechar
          try {
            sessionStorage.removeItem(`corel:snapshot:${QUOTATION_PEDIDO_ID}:${QUOTATION_ITEM_ID}`);
            sessionStorage.removeItem(`corel:dist:v1:${QUOTATION_PEDIDO_ID}:${QUOTATION_ITEM_ID}`);
            sendToTerminal('🧹 [QUOTATION] Snapshot limpo ao fechar', {}, LogCategory.BUSINESS);
          } catch { }

          onBack();
        }}
        onNewQuotation={() => {
          // Resetar estados e reiniciar o pipeline (fluxo normal desta unidade).
          QuotationOperationContextStore.clear();
          setRoutedClient(null);
          setPageState('idle');
          setSelectedRule(null);
          setSelectedQuality(null);
          setSavingRule(null);
          setCollectedObjects([]);
          setDetectionResult(null);
          setErrorMessage(null);

          // Limpar snapshot
          try {
            sessionStorage.removeItem(`corel:snapshot:${QUOTATION_PEDIDO_ID}:${QUOTATION_ITEM_ID}`);
            sessionStorage.removeItem(`corel:dist:v1:${QUOTATION_PEDIDO_ID}:${QUOTATION_ITEM_ID}`);
          } catch { }

          sendToTerminal('🔄 [QUOTATION] Novo orçamento iniciado', {}, LogCategory.BUSINESS);
          startFlow();
        }}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: TELA 2 - Modal de confirmação de material
  // ═══════════════════════════════════════════════════════════════════════════

  if (pageState === 'confirmMaterial' && detectionResult?.rule) {
    // ✅ Calcular métricas totais apenas quando exibir modal (sem useMemo para evitar hooks dentro de condicional)
    let totalMetrics: { totalHeightM: number } | null = null;

    if (collectedObjects.length > 0) {
      const totalHeightCm = collectedObjects.reduce((sum, obj) => {
        const height = obj.height ?? 0;
        const mult = (obj as any).multiplicador ?? 1;
        return sum + (height * mult);
      }, 0);

      const totalHeightM = totalHeightCm / 100;
      totalMetrics = { totalHeightM };
    }

    return (
      <MaterialConfirmModal
        isOpen={true}
        detectionResult={detectionResult}
        totalMetrics={totalMetrics ?? undefined}
        onConfirm={handleConfirmMaterial}
        onReject={handleRejectMaterial}
        onClose={onBack}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: TELA 2 (alternativa) - Modal de seleção manual de material
  // ═══════════════════════════════════════════════════════════════════════════

  // Renderiza mesmo com rules vazio: o modal exibe o estado vazio amigável
  // ("nenhum material habilitado para orçamento") em vez de tela em branco/erro.
  if (pageState === 'selectMaterial') {
    return (
      <MaterialSelectModal
        isOpen={true}
        rules={rules}
        onSelect={handleSelectMaterial}
        onClose={onBack}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER: TELA 3 - Modal de seleção de qualidade
  // ═══════════════════════════════════════════════════════════════════════════

  if (pageState === 'selectQuality') {
    // O cálculo EXIGE qualidade_id > 0, então não podemos pular esta etapa

    // Se não temos categoriaId, não há qualidades disponíveis
    const categoriaId = selectedRule?.produto?.categoria_item;

    if (!categoriaId) {
      // Produto sem categoria - mostrar modal informando
      return (
        <QualitySelectModal
          isOpen={true}
          qualities={[]}
          onSelect={handleSelectQuality}
          onClose={() => {
            sendToTerminal('👋 [QUOTATION] Usuário cancelou seleção de qualidade', {}, LogCategory.BUSINESS);
            onBack();
          }}
          error="Este produto não possui categoria configurada. Não há qualidades disponíveis."
          isLoading={false}
          onRetry={handleRetryQualities}
          onSkip={handleSkipQuality}
        />
      );
    }

    // ✅ CORREÇÃO: Mostrar loading mínimo até que o hook tenha tentado carregar
    // Isso evita mostrar erro antes do carregamento começar
    const shouldShowLoading = qualitiesLoadingFromHook || !qualitiesHasAttemptedFromHook;

    // ✅ Mostrar modal - passa isLoading do hook para exibir loading interno
    return (
      <QualitySelectModal
        isOpen={true}
        qualities={qualitiesFromHook}
        onSelect={handleSelectQuality}
        onClose={() => {
          sendToTerminal('👋 [QUOTATION] Usuário cancelou seleção de qualidade', {}, LogCategory.BUSINESS);
          onBack();
        }}
        error={qualitiesErrorFromHook}
        isLoading={shouldShowLoading}
        onRetry={handleRetryQualities}
        onSkip={handleSkipQuality}
      />
    );
  }

  // Fallback final para estados de transição (ex.: confirmMaterial/selectMaterial
  // brevemente sem dados). NUNCA retornar null aqui: com a flag showQuotationCalculator
  // ainda true, um render null deixaria a tela em branco e o atalho "7" não recuperaria.
  // Mantemos um overlay visível de carregamento em vez de tela vazia.
  return (
    <PreparationOverlay
      visible={true}
      steps={PREPARATION_STEPS}
      currentStep="rules"
      error={null}
      title="Calculando Orçamento"
    />
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NOTA: QuotationDetailsView e MetricCard foram removidos
// Agora usamos QuotationSummaryPage para exibir o resumo do orçamento
// ═══════════════════════════════════════════════════════════════════════════
